import { StoreState } from "enum/StoreState";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useStores } from "stores/setup/use-store";

export const usePhoneVerificationModal = () => {
    const [step, setStep] = useState<"form" | "code">("form");
    const [phoneData, setPhoneData] = useState<{ phone?: string; code?: string }>({});
    const [enteredCode, setEnteredCode] = useState("");
    const [isCodeValid, setIsCodeValid] = useState(true);
    const [resendTimer, setResendTimer] = useState(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    const [isPhoneAlreadyUsed, setIsPhoneAlreadyUsed] = useState(false);

    const {
        dataStores: { userVerifyStore, userConfigStore },
    } = useStores();

    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (resendTimer > 0 && !intervalRef.current) {
            intervalRef.current = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current!);
                        intervalRef.current = null;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [resendTimer]);

    const handleSendCode = useCallback(async () => {
        const { code, phone } = phoneData;

        if (!code || !phone) return;

        const phoneNumber = "+" + code + phone;

        const startVerification = await userVerifyStore.startPhoneVerification(
            phoneNumber
        );

        if (!startVerification) return;

        if (startVerification.error) {
            setIsBlocked(true);
        } else {
            setIsBlocked(false);
        }
    }, [phoneData, userVerifyStore]);

    const handleResendCode = useCallback(() => {
        handleSendCode();
        setResendTimer(30);
    }, [handleSendCode]);

    const handleCodeSubmit = useCallback(async () => {
        const { code, phone } = phoneData;

        if (!code || !phone) return;

        const phoneNumber = "+" + code + phone;

        const checkedVerification = await userVerifyStore.checkPhoneVerification(
            phoneNumber,
            enteredCode
        );

        if (!checkedVerification) return;

        if (checkedVerification.error) {
            setIsBlocked(true);
            return;
        }

        if (checkedVerification?.success) {
            return true;
        } else {
            setEnteredCode("");
            setIsCodeValid(false);
            return false;
        }
    }, [phoneData, enteredCode, userVerifyStore, userConfigStore]);

    const resetState = useCallback(() => {
        setStep("form");
        setEnteredCode("");
        setIsCodeValid(true);
        setResendTimer(0);
        setIsBlocked(false);
    }, []);

    const isLoading = useMemo(() => {
        return (
            userVerifyStore.state === StoreState.PEDDING ||
            userConfigStore.state === StoreState.PEDDING
        );
    }, [userVerifyStore.state, userConfigStore.state]);

    return {
        step,
        setStep,
        phoneData,
        setPhoneData,
        enteredCode,
        setEnteredCode,
        isCodeValid,
        setIsCodeValid,
        resendTimer,
        setResendTimer,
        isBlocked,
        setIsBlocked,
        handleSendCode,
        handleResendCode,
        handleCodeSubmit,
        resetState,
        isLoading,
        consentChecked,
        setConsentChecked,
        isPhoneAlreadyUsed,
        setIsPhoneAlreadyUsed
    };
};